/**
 * Settings catalog — the single source of truth for the settings list.
 *
 * Consumed by SettingsLandingPage (renders the cards) and, going forward,
 * the global search index. Add a setting here once and it shows up in both.
 *
 * Each link's `to` is relative to `/:tenant/` (it already includes the
 * `manage/...` segment), e.g. `manage/settings/fees`.
 */
import React from "react";
import {
  Settings as SettingsIcon,
  CreditCard,
  Workflow,
  Mail,
  ClipboardList,
  Wrench,
  Plug,
  Building2,
} from "lucide-react";

export interface SettingsLink {
  label: string;
  to: string; // relative to /:tenant/
  description?: string;
  /**
   * Other words staff type when they are looking for this page ("disclaimers"
   * for Waivers, "tuition" for Pricing). Matched by the sidebar search, never
   * displayed. One alias per entry; lower case; a phrase is fine.
   */
  keywords?: string[];
}

export interface SettingsCategory {
  title: string;
  icon: React.ReactNode;
  iconColor: string;
  links: SettingsLink[];
}

export const settingsCategories: SettingsCategory[] = [
  {
    title: "General",
    icon: <SettingsIcon className="w-5 h-5" />,
    iconColor: "text-gray-500",
    links: [
      {
        label: "General Settings",
        to: "manage/settings",
        description: "Branding, business info, core toggles",
        keywords: ["logo", "colors", "button colors", "business name", "contact info", "time zone", "brand"],
      },
      {
        label: "Your Domain",
        to: "manage/settings/domain",
        description: "Show your club's site on your own web address",
        keywords: ["custom domain", "website address", "dns", "url"],
      },
      { label: "Roles", to: "manage/roles", description: "Staff roles and permissions", keywords: ["access", "staff access"] },
      { label: "Activity Log", to: "manage/activity", description: "Who changed what, when", keywords: ["audit", "history", "changes"] },
    ],
  },
  {
    title: "Club Central Account",
    icon: <Building2 className="w-5 h-5" />,
    iconColor: "text-gray-500 dark:text-gray-400",
    links: [
      {
        label: "Your Plan",
        to: "manage/settings/subscription",
        description: "Subscription tier, add-ons, AI plan & features",
        keywords: ["upgrade", "club central bill"],
      },
    ],
  },
  {
    title: "Billing & Payments",
    icon: <CreditCard className="w-5 h-5" />,
    iconColor: "text-green-500",
    links: [
      {
        label: "Fees",
        to: "manage/settings/fees",
        keywords: ["registration fee", "annual fee", "late fee", "card fee", "processing fee", "charges"],
      },
      { label: "Pricing", to: "manage/settings/pricing", keywords: ["tuition", "rates", "price tiers", "monthly dues", "discounts"] },
      { label: "Commissions", to: "manage/settings/commissions", keywords: ["payouts", "store commission"] },
      {
        label: "Stripe Killswitch",
        to: "manage/settings/stripe-killswitch",
        description: "Emergency payment shutoff",
        keywords: ["stop payments", "pause payments", "turn off payments", "disable payments"],
      },
    ],
  },
  {
    title: "Automation",
    icon: <Workflow className="w-5 h-5" />,
    iconColor: "text-purple-500",
    links: [
      { label: "Automation", to: "manage/settings/automation", description: "Reminders, follow-ups, scheduled jobs", keywords: ["automatic emails", "nightly jobs"] },
      { label: "Inactivity Automation", to: "manage/settings/inactivity-automation", keywords: ["inactive members", "lapsed", "not attending"] },
      { label: "Scheduled Cancellations", to: "manage/scheduled-cancellations", keywords: ["cancel membership", "quit", "withdraw", "drop"] },
    ],
  },
  {
    title: "Communication",
    icon: <Mail className="w-5 h-5" />,
    iconColor: "text-blue-500",
    links: [
      { label: "Notifications", to: "manage/settings/notifications", keywords: ["alerts", "text messages", "sms", "who gets emailed"] },
      // 2026-10-14: Chris could not find where the final notice is controlled.
      { label: "Past-due reminders", to: "manage/settings/notifications?role=member", description: "When the reminders and final notice go out to families who owe; preview who would be emailed", keywords: ["final notice", "overdue", "past due", "dunning", "late payment reminder", "collections", "balance reminder"] },
      // 2026-10-20: Chris searched for the switch that stops "unenrollment"
      // emails to parents and found nothing. It is a Member (family) email.
      // Its own address (the extra parameter is ignored by the page): search
      // keeps one result per address, so sharing Past-due reminders' address
      // would show it under that name.
      { label: "Enrollment Ended email", to: "manage/settings/notifications?role=member&notice=enrollment-ended", description: "Turn off the email families get when staff take their athlete out of a class", keywords: ["unenroll", "unenrollment", "unenrollment notification", "enrollment notification", "enrollment ended", "parent email", "family email", "class change"] },
      { label: "Enrollment Changed email", to: "manage/settings/notifications?role=member&notice=enrollment-changed", description: "Turn off the one email families get when staff move their athlete to a different class", keywords: ["class change", "move class", "moved", "transfer", "enrollment changed", "parent email", "family email"] },
      { label: "Email Settings", to: "manage/settings/email", description: "Who gets staff emails, missing attendance alerts, extra recipients", keywords: ["staff emails", "email recipients", "missing attendance"] },
      { label: "Email Templates", to: "manage/settings/email-templates", keywords: ["email wording", "welcome email", "message templates", "final notice", "past-due reminder", "reminder email"] },
      { label: "Email Verification", to: "manage/settings/email-verification", keywords: ["verify email", "bounced emails", "bad email addresses"] },
      { label: "Support Routing", to: "manage/settings/support", keywords: ["help requests", "support tickets", "feedback"] },
    ],
  },
  {
    title: "Registration & Programs",
    icon: <ClipboardList className="w-5 h-5" />,
    iconColor: "text-amber-500",
    links: [
      {
        label: "Waivers",
        to: "manage/settings/waivers",
        description: "Forms families sign: liability release, consent, policies",
        // 2026-10-07 walkthrough: staff looked for this page under these words.
        keywords: ["disclaimers", "disclaimer", "release", "liability", "consent", "sign", "signature", "policies", "agreement"],
      },
      { label: "Task Types", to: "manage/settings/action-types", keywords: ["action types", "to-do", "follow-up types"] },
      { label: "Private Sessions", to: "manage/settings/private-sessions", keywords: ["private lessons", "privates", "one on one"] },
    ],
  },
  {
    title: "Operations",
    icon: <Wrench className="w-5 h-5" />,
    iconColor: "text-cyan-500",
    links: [
      { label: "Data Health", to: "manage/data-health", description: "Automated checks on your data", keywords: ["duplicates", "data problems", "missing information"] },
      { label: "Facility Bookings", to: "manage/facilities", keywords: ["rentals", "rooms", "parties", "reserve space"] },
      { label: "Kiosk Settings", to: "manage/settings/kiosk", keywords: ["check-in", "front desk", "tablet", "sign in screen"] },
    ],
  },
  {
    title: "Store & Integrations",
    icon: <Plug className="w-5 h-5" />,
    iconColor: "text-pink-500",
    links: [
      { label: "Storefront, POS & Shipping", to: "manage/settings/pos", keywords: ["store", "shop", "pro shop", "point of sale", "merchandise", "register"] },
      { label: "Store Settings", to: "manage/settings/store", description: "Low stock threshold and SKU numbering", keywords: ["inventory", "low stock", "sku"] },
      { label: "GP Integration", to: "manage/settings/store/gp" },
      { label: "Integrations", to: "manage/settings/integrations", keywords: ["connections", "connect", "api keys", "third party"] },
    ],
  },
];
